import DottedMap from "dotted-map";

import countriesData from "@/assets/countries.json";
import countryBoxes from "@/assets/country-bboxes.json";
import type { DashboardCountry } from "@/types/dashboard";

/* One world map for the whole dashboard. Its projection places both the
   dotted land layer and the affiliate circles, so the two always line up. */
const worldMap = new DottedMap({ height: 100, grid: "diagonal" });

/** The map's viewBox, in the library's own units. */
export const MAP_W: number = worldMap.image.width;
export const MAP_H: number = worldMap.image.height;

export function projectPoint(lat: number, lng: number) {
  const { x, y } = worldMap.getPin({ lat, lng });
  return { x, y };
}

const baseMapCache = new Map<string, string>();
/** The dotted land layer, rendered once per theme. */
export function baseMapSvg(dark: boolean) {
  const key = dark ? "dark" : "light";
  let svg = baseMapCache.get(key);
  if (!svg) {
    svg = worldMap.getSVG({
      radius: 0.22,
      color: dark ? "#FFFFFF40" : "#00000040",
      shape: "circle",
      backgroundColor: "transparent",
    });
    baseMapCache.set(key, svg);
  }
  return svg;
}

/* Per-country land bounding boxes, keyed by ISO 3166 alpha-2 as stored on
   affiliate profiles. They were precomputed from the world geometry that
   ships with dotted-map in an 800 x 400 equirectangular frame, so they are
   rescaled here into the map's units (360° of longitude across MAP_W, and
   the map's latitude span across MAP_H). */
const latSpan = worldMap.image.region.lat.max - worldMap.image.region.lat.min;
const lngSpan = worldMap.image.region.lng.max - worldMap.image.region.lng.min;
const SCALE_X = (MAP_W / lngSpan) * (360 / 800);
const SCALE_Y = (MAP_H / latSpan) * (180 / 400);
const bboxByIso2 = new Map<string, { w: number; h: number }>(
  Object.entries(countryBoxes as Record<string, number[]>).map(([code, box]) => [
    code,
    { w: (box[0] ?? 0) * SCALE_X, h: (box[1] ?? 0) * SCALE_Y },
  ])
);

export const DOT_MIN_RADIUS = 0.9;
export const DOT_MAX_RADIUS = 5;

/**
 * Largest circle a country can carry without spilling far past its own
 * borders: its diameter stays under its shorter bounding-box side. Countries
 * missing from the geometry (small islands and city states) stay at the minimum.
 */
export function countryRadiusCap(iso2: string): number {
  const box = bboxByIso2.get(iso2.toUpperCase());
  if (!box) return DOT_MIN_RADIUS;
  return Math.max(DOT_MIN_RADIUS, Math.min(DOT_MAX_RADIUS, 0.45 * Math.min(box.w, box.h)));
}

/** Square-root scale so area, not radius, tracks the count; capped per country. */
export function dotRadius(count: number, maxCount: number, iso2: string): number {
  if (count <= 0 || maxCount <= 0) return DOT_MIN_RADIUS;
  const scaled = DOT_MIN_RADIUS + (DOT_MAX_RADIUS - DOT_MIN_RADIUS) * Math.sqrt(count / maxCount);
  return Math.min(scaled, countryRadiusCap(iso2));
}

const countryByCode = new Map(
  (countriesData as Array<{ code: string; name: string; geo?: { latitude: number; longitude: number } }>).map(
    (c) => [c.code.toUpperCase(), c]
  )
);

function titleCase(value: string) {
  if (value.length <= 3) return value.toUpperCase();
  return value
    .toLowerCase()
    .replace(/(^|[\s(-])([a-z])/g, (_, pre: string, ch: string) => pre + ch.toUpperCase());
}

export function countryName(iso2: string): string {
  if (!iso2 || iso2 === "Unknown") return "No country on profile";
  const c = countryByCode.get(iso2.toUpperCase());
  return c ? titleCase(c.name) : iso2;
}

export function countryCentroid(iso2: string): { lat: number; lng: number } | null {
  const c = countryByCode.get(iso2.toUpperCase());
  return c?.geo ? { lat: c.geo.latitude, lng: c.geo.longitude } : null;
}

export interface MapCountry extends DashboardCountry {
  name: string;
  x: number;
  y: number;
  r: number;
}

/** Countries with a known position, placed and sized for the map. */
export function placeCountries(countries: DashboardCountry[]): MapCountry[] {
  const max = Math.max(0, ...countries.map((c) => c.affiliates));
  const placed: MapCountry[] = [];
  for (const c of countries) {
    const centroid = countryCentroid(c.country);
    if (!centroid) continue;
    const { x, y } = projectPoint(centroid.lat, centroid.lng);
    placed.push({ ...c, name: countryName(c.country), x, y, r: dotRadius(c.affiliates, max, c.country) });
  }
  // Small circles on top so a large neighbour never hides them.
  return placed.sort((a, b) => b.r - a.r);
}
